import Axios from 'axios'
import { msgMap, getMsg, msgType } from '@g/msgs.js'
import { captureException } from '@g/utils/sentryInstall'
import { getWebVersionHeaderName, isMagicbuyProject } from '@g/utils/projectUtils'
import { getApiErrorMessage } from '@g/utils/apiError'
import { isMaintenanceResponse, normalizeMaintenanceInfo, MAINTENANCE_ERROR_CODE } from '@g/utils/maintenance'
import { createImmediateUnauthorizedDiagnostic, queueAuthDiagnostic } from '@g/utils/authStorage'
import { reportAuthDiagnostic } from '@g/utils/authDiagnostic'

import {
  isInApp,
  actGetDeviceUDID,
  actGetDeviceType,
  actGetDeviceName,
  actHapticFeedback,
  actCheckWebToUpdate,
  actWebToUpdate,
} from '../utils/appSDK'
import { genRandStr } from '../utils/index'

const axiosProvider = Axios.create({
  baseURL: window.G_BASEURL.API_JYB_BASE_URL,
  timeout: 120000,
  timeRetry: true, // 超时重新请求
  retry: 5, // 请求次数
})

export async function setDefaultHeader () {
  if (isInApp()) { // APP 需要带上设备信息
    console.log('setDefaultHeader 开始获取设备信息')
    await Promise.all([
      actGetDeviceUDID(),
      actGetDeviceType(),
      actGetDeviceName(),
    ]).then((res) => {
      console.log('setDefaultHeader 设备信息：', res)
      axiosProvider.defaults.headers.common['device-udid'] = encodeURI(res[0])
      axiosProvider.defaults.headers.common['device-type'] = encodeURI(res[1])
      axiosProvider.defaults.headers.common['device-name'] = encodeURI(res[2])
    })
  } else {
    if (!localStorage.deviceUdid) {
      const genUdid = () => {
        let udid = Math.floor(Math.random() * 1000).toString(16)

        udid += Date.now().toString(36)
        return udid
      }
      localStorage.deviceUdid = genUdid()
    }
    axiosProvider.defaults.headers.common['device-udid'] = encodeURI(localStorage.deviceUdid)
  }
}

// 請求拦截
axiosProvider.interceptors.request.use(function (config) {
  const project = window.vueApp?.$store.state.project
  if (isMagicbuyProject(project)) {
    config.headers['Tsmd-Site'] = 'MG'
  } else {
    config.headers['Tsmd-Site'] = 'KK'
  }
  if (isInApp() && localStorage.webVer && config.url.indexOf('option/app') === -1) {
    const versionHeaderName = getWebVersionHeaderName(project)
    if (versionHeaderName) {
      config.headers[versionHeaderName] = localStorage.webVer
    }
  }
  if (config.method === 'post' && !config.headers['Req-Id']) {
    config.headers['Req-Id'] = genRandStr()
  }
  // if (localStorage.getItem('accessToken') && config.url.indexOf('accessToken') === -1) {
  //   // 每次請求增加 query 参数 accessToken
  //   config.url += (config.url.indexOf('?') === -1 ? '?' : '&') +
  //   'accessToken=' + encodeURIComponent(localStorage.getItem('accessToken'))
  // }
  if (config.params?.config) {
    Object.assign(config, config.params.config)
    delete config.params.config
  }
  if (config.data?.config) {
    Object.assign(config, config.data.config)
    delete config.data.config
  }
  if (localStorage.getItem('accessToken')) {
    config.headers.Authorization = 'Bearer ' + localStorage.getItem('accessToken')
  }
  config.startTime = Date.now()

  return config
})

// 超时重新请求拦截
axiosProvider.interceptors.response.use((res) => {
  return res
}, (err) => {
  // if (err.message.indexOf('timeout') === -1) {
  //   return Promise.reject(err)
  // }
  if (err.response) {
    return Promise.reject(err)
  }
  const config = err.config
  // If config does not exist or the retry option is not set, reject
  if (!config || !config.timeRetry) return Promise.reject(err)

  // Set the variable for keeping track of the retry count
  config.__retryCount = config.__retryCount || 0

  // Check if we've maxed out the total number of retries
  if (config.__retryCount >= config.retry) {
    // Reject with the error
    return Promise.reject(err)
  }

  // Increase the retry count
  config.__retryCount += 1

  // Create new promise to handle exponential backoff
  const backoff = new Promise(function (resolve) {
    // const RETRY_TIMEOUTS = [1, 1, 1, 1, 1, 1, 1] // seconds
    // const delay = (RETRY_TIMEOUTS[config.retryCount] || 5) * 1000

    setTimeout(function () {
      resolve()
    }, 100)
  })

  // Return the promise in which recalls axios to retry the request
  return backoff.then(() => {
    const c = { ...config }
    c.params = { ...c.params, _t: Date.now() }
    c.startTime = Date.now()

    return axiosProvider(c).catch(err => {
      err.config.noInterceptorError = true
      return Promise.reject(err)
    })
  })
})

// 任一接口返回 maintenance===true 即写入根 store，由 MaintenancePage 全屏接管
function activateMaintenance (data) {
  const store = window.vueApp?.$store
  if (!store) return
  store.commit('setMaintenance', normalizeMaintenanceInfo(data))
}

let isWebUpdating = false
// 响应拦截
axiosProvider.interceptors.response.use(async function (res) {
  if (isMaintenanceResponse(res.data)) {
    activateMaintenance(res.data)
    // 维护页已接管画面，不再弹错；标记 error 让调用方走既有失败分支，避免读取不存在的 model
    if (!res.data.error) {
      res.data.error = MAINTENANCE_ERROR_CODE
    }
    return res
  }

  if (res.data.error) {
    if (!res.config.noShowError) {
      window.vueApp.$snackbar(getApiErrorMessage(res), { color: 'error' })
    }
    console.log(res.data.error)
  } else if (res.data.tsmdResult === 'ERROR') {
    if (res.data.name && res.data.name.indexOf('-H5VerOlder') > -1) {
      if (!isWebUpdating && isInApp()) {
        try {
          isWebUpdating = true
          const res = await window.vueApp.$store.dispatch('option/app')
          if (!res.data.error) {
            const url = res.data.model.fe_h5_download
            await actWebToUpdate({
              version: '1',
              dowLoadUrl: url,
              zipSize: `${res.data.model.fe_h5_size}MB`
            })
          }
          // window.vueApp.$toast('由於當前版本過低，需要升級到最新版本', {
          //   duration: 10000,
          // })

          // await actCheckWebToUpdate()
        } finally {
          isWebUpdating = false
        }
      }
    // window.vueApp.$confirm('由於當前版本過低，需要升級到最新版本', {
      //   buttonFalseText: false,
      // }).then(r => {
      //   if (r) {
      //   }
      // })
    } else if (msgMap[res.data.name]) {
      const msgItem = msgMap[res.data.name]
      res.data.error = res.data.name
      if (msgItem.type === msgType.alert || msgItem.type === msgType.confirm) {
        const code = res.data.error = res.data.name
        if (code === 'RcvrTransferInOrd') {
          msgItem.handle && msgItem.handle(JSON.parse(res.config.data))
        } else {
          msgItem.handle && msgItem.handle(res)
        }
      } else if (msgItem.type === msgType.noaction) {
        actHapticFeedback('1', 300)
      }
    } else if (res.data.name) {
      if (!res.config.noShowError) {
        if (localStorage.accessToken) {
          window.vueApp.$snackbar(res.data.name, { color: 'error' })
        } else {
          window.vueApp.$snackbar(res.data.message, { color: 'error' })
        }
        setTimeout(() => {
          window.vueApp.$eventBus.$emit(window.vueApp.$eventType.ErrorReportScreenshots, {
            data: res.data,
          })
        }, 200)
      }
      res.data.error = res.data.name
    } else {
      let message = res.data.message
      if (res.data.list && res.data.list.length) {
        message = res.data.list.map(item => item.message).join('<br/>')
      }
      if (!res.config.noShowError) {
        window.vueApp.$snackbar('Error (2xx):<br/>' + message, { color: 'error' })
        // setTimeout(() => {
        //   window.vueApp.$eventBus.$emit(window.vueApp.$eventType.ErrorReportScreenshots, {
        //     data: res.data,
        //   })
        // }, 200)
      }
      res.data.error = message
    }
  }

  return res
}, function (error) {
  // 网关以 503 等非 2xx 回 { maintenance: true } 时同样进入维护页，不弹错、不登出、不上报
  if (error.response && isMaintenanceResponse(error.response.data)) {
    activateMaintenance(error.response.data)
    return Promise.reject(error)
  }

  if (error.config.noInterceptorError) {
    return Promise.reject(error)
  }

  if (error.response) {
    console.log(error.response)
    // 认证失败，登出
    if (error.response.status === 401) {
      // 游客页面（买即可新版首页 / 落地页）主动探测需登录的接口时，不弹提示也不触发登出跳转，由页面自行处理
      if (error.config && error.config.skipUnauthorizedHandling) {
        return Promise.reject(error)
      }
      const inApp = isInApp()
      const authDiagnostic = createImmediateUnauthorizedDiagnostic({
        project: window.vueApp?.$store.state.project,
        inApp,
        requestUrl: error.config && error.config.url,
        requestStartedAt: error.config && error.config.startTime,
        hadAuthorization: Boolean(error.config && error.config.headers && error.config.headers.Authorization),
      })
      if (authDiagnostic) {
        reportAuthDiagnostic(authDiagnostic)
        queueAuthDiagnostic(authDiagnostic)
      }
      const msgText = authDiagnostic
        ? '登入驗證失敗，請依畫面提示回報問題'
        : (localStorage.accessToken ? '登錄失效，請重新登錄' : '請先登錄')

      if (window.isPc) {
        window.vueApp.$snackbar(msgText, { color: 'error' })
      } else {
        window.vueApp.$toast(msgText)
      }
      window.vueApp.$store.dispatch('logout')
    } else if (!error.config.noShowError) {
      window.vueApp.$snackbar(error.response.status + ' ' + error.response.data.name + '<br/>' + error.response.data.message, { color: 'error' })
    }
    if (error.response.status >= 500) {
      captureException(error, {
        tags: {
          error_position: 'axiosProvider',
          status: String(error.response.status),
          url: error.config.url,
        },
      })
    }
  } else if (!error.config.noShowError) {
    window.vueApp.$eventBus.$emit(window.vueApp.$eventType.Offline)
    // if (!error.config.noShowError) { // 是否处理错误信息
    //   if (error.config.startTime + 100 > Date.now()) {
    //     if (error.message.indexOf('timeout') > -1) {
    //       window.vueApp.$snackbar('Error:<br/>網路超時，請重試一次', { color: 'error', time: 5000 })
    //     } else {
    //       window.vueApp.$snackbar('Error:<br/>網路有點問題，請重試或聯繫客服', { color: 'error', time: 5000 })
    //     }
    //   }
    // }
  }

  return Promise.reject(error)
})

export default axiosProvider
